import { Box, HStack, Text, Typography } from "components/ui";
import React from "react";
import { useIsMobile } from "utils/hooks/useIsMobile";
import PaperDashboardCard from "components/general/PaperDashboardCard";
import InfoPanel from "components/general/InfoPanel";
import { Easing, ViewStyle } from "react-native";
import Animated, { useAnimatedStyle, useSharedValue, withRepeat, withTiming, interpolate } from "react-native-reanimated";
import { LinearGradient } from "expo-linear-gradient";
import { MapPinned } from "lucide-react-native";
import { router } from "expo-router";

interface Panel {
	description: string;
	value: number;
	icon: React.ReactNode;
	hasAlert?: boolean;
	link?: string;
}

interface EquipmentAndSitesSectionProps {
	equipmentPanelList: Panel[];
	equipmentsIsLoading: boolean;
	sitesAndGroupsPanelList: Panel[];
	siteIsLoading: boolean;
	sites: any;
}

const SkeletonLoader = () => {
	const isMobile = useIsMobile();
	const shimmerValue = useSharedValue(0);
	const pulseValue = useSharedValue(0);

	React.useEffect(() => {
		shimmerValue.value = withRepeat(withTiming(1, { duration: 1800, easing: Easing.inOut(Easing.ease) }), -1, true);
		pulseValue.value = withRepeat(withTiming(1, { duration: 1200, easing: Easing.inOut(Easing.ease) }), -1, true);
	}, []);

	const gradientStyle = useAnimatedStyle(() => ({
		position: "absolute",
		top: 0,
		bottom: 0,
		left: 0,
		right: 0,
		transform: [{ translateX: interpolate(shimmerValue.value, [0, 1], [-300, 300]) }],
	}));

	const pulseStyle = useAnimatedStyle(() => ({
		opacity: 0.3 + pulseValue.value * 0.4,
		backgroundColor: "#e0e0e0",
	}));

	interface ShimmerBoxProps {
		width: ViewStyle["width"];
		height: ViewStyle["height"];
		borderRadius?: number;
		marginBottom?: number;
	}

	const ShimmerBox = ({ width, height, borderRadius = 8, marginBottom }: ShimmerBoxProps) => (
		<Box height={height} width={width} borderRadius={borderRadius} overflow="hidden" mb={marginBottom}>
			<Animated.View style={[pulseStyle, { position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }]} />
			<Animated.View style={gradientStyle}>
				<LinearGradient colors={["rgba(255,255,255,0)", "rgba(255,255,255,0.8)", "rgba(255,255,255,0)"]} start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }} style={{ width: 300, height: "100%" }} />
			</Animated.View>
		</Box>
	);

	const CardSkeleton = () => (
		<Box bg="$basicwhite" borderRadius={12} padding={28} overflow="hidden">
			<ShimmerBox width={120} height={32} marginBottom={16} />
			<ShimmerBox width="80%" height={60} marginBottom={24} />
			<Box gap={12}>
				{[1, 2, 3].map((_, index) => (
					<ShimmerBox key={index} width="100%" height={80} />
				))}
			</Box>
		</Box>
	);

	return (
		<>
			<Box borderRadius={12} bg="$neutral35">
				<Box padding={28} flexDirection="row">
					<Typography.H2>Equipment & Sites</Typography.H2>
				</Box>
			</Box>
			<Box flexDirection="row" gap={28} padding={28}>
				<Box flex={1}>
					<CardSkeleton />
				</Box>
				<Box flex={1}>
					<CardSkeleton />
				</Box>
			</Box>
		</>
	);
};

const EquipmentAndSitesSection = ({ equipmentPanelList, equipmentsIsLoading, sitesAndGroupsPanelList, siteIsLoading, sites }: EquipmentAndSitesSectionProps) => {
	const isMobile = useIsMobile();

	if (equipmentsIsLoading || siteIsLoading) {
		return <SkeletonLoader />;
	}

	return (
		<>
			<Box borderRadius={12} bg="$neutral35">
				<HStack gap={24} alignItems="center" padding={28} flexDirection="row">
					<MapPinned size={36} />
					<Typography.H2>Equipment & Sites</Typography.H2>
				</HStack>
			</Box>
			<Box flexDirection={isMobile ? "column" : "row"} gap={28} padding={28}>
				<Box flex={isMobile ? "initial" : 1}>
					<PaperDashboardCard
						title="Equipment"
						description=""
						hideButton
						component={
							<Box gap={12}>
								{equipmentPanelList.map((panel: Panel, index: number) => (
									<InfoPanel key={index} {...panel} />
								))}
							</Box>
						}
						buttonLabel={""}
						buttonAction={() => {}}
					/>
				</Box>
				<Box flex={isMobile ? "initial" : 1}>
					<PaperDashboardCard
						title="Sites and groups"
						description={`You have ${sites?.withActivity} active sites in the last 12 months.`}
						buttonLabel="All sites"
						buttonAction={() => {
							router.push("/sites");
						}}
						flex={1}
						component={
							<Box gap={12}>
								{sitesAndGroupsPanelList.map((panel: Panel, index: number) => (
									<InfoPanel key={index} {...panel} />
								))}
							</Box>
						}
					/>
				</Box>
			</Box>
		</>
	);
};

export default EquipmentAndSitesSection;
